import React from 'react';
import { SectionSchema, SchemaField, SectionData } from './types';
import CollectionChooser from './fields/collectionChooser';
import ProductChooser from './fields/productChooser';
import CategoryChooser from './fields/categoryChooser';
import ImageURLField from './fields/imageUrlField';

interface DynamicSectionEditorProps {
  schema: SectionSchema;
  data: SectionData;
  onChange: (data: SectionData) => void;
}

const DynamicSectionEditor: React.FC<DynamicSectionEditorProps> = ({
  schema,
  data,
  onChange
}) => {
  const handleFieldChange = (fieldName: string, value: any) => {
    onChange({ ...data, [fieldName]: value });
  };

  const handleNestedFieldChange = (parentField: string, fieldName: string, value: any) => {
    onChange({
      ...data,
      [parentField]: {
        ...(data[parentField] || {}),
        [fieldName]: value
      }
    });
  };

  const handleArrayItemChange = (fieldName: string, index: number, subFieldName: string, value: any) => {
    const newArray = [...(data[fieldName] || [])];
    if (!newArray[index]) {
      newArray[index] = {};
    }
    
    if (subFieldName.includes('.')) {
      // Handle deeply nested fields (e.g., primaryButton.text)
      const [parentField, childField] = subFieldName.split('.');
      newArray[index] = {
        ...newArray[index],
        [parentField]: {
          ...(newArray[index][parentField] || {}),
          [childField]: value
        }
      };
    } else {
      // Handle direct fields
      newArray[index] = {
        ...newArray[index],
        [subFieldName]: value
      };
    }
    
    onChange({ ...data, [fieldName]: newArray });
  };

  const handleAddArrayItem = (fieldName: string, defaultValue: any) => {
    const newArray = [...(data[fieldName] || [])];
    newArray.push(defaultValue);
    onChange({ ...data, [fieldName]: newArray });
  };

  const handleRemoveArrayItem = (fieldName: string, index: number) => {
    const newArray = [...(data[fieldName] || [])];
    newArray.splice(index, 1);
    onChange({ ...data, [fieldName]: newArray });
  };

  const renderField = (fieldName: string, fieldSchema: SchemaField, value: any, parentField?: string) => {
    const fieldLabel = fieldSchema.label || fieldName.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');

    const fieldKey = parentField ? `${parentField}.${fieldName}` : fieldName;
    const commonProps = {
      className: "mb-4"
    };

    const handleValueChange = (newValue: any) => {
      if (parentField?.includes('.')) {
        const [arrayField, index] = parentField.split('.');
        if (fieldName.includes('.')) {
          // Handle nested fields within array items (e.g., primaryButton.text)
          handleArrayItemChange(arrayField, parseInt(index), fieldName, newValue);
        } else {
          // Handle direct fields within array items
          handleArrayItemChange(arrayField, parseInt(index), fieldName, newValue);
        }
      } else if (parentField) {
        handleNestedFieldChange(parentField, fieldName, newValue);
      } else {
        handleFieldChange(fieldName, newValue);
      }
    };

    switch (fieldSchema.type) {
      case 'text':
        return (
          <div key={fieldKey} {...commonProps}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {fieldLabel}
            </label>
            <input
              type="text"
              value={value || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
            {fieldSchema.description && (
              <p className="mt-1 text-sm text-gray-500">{fieldSchema.description}</p>
            )}
          </div>
        );

      case 'longtext':
        return (
          <div key={fieldKey} {...commonProps}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {fieldLabel}
            </label>
            <textarea
              value={value || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              rows={4}
            />
            {fieldSchema.description && (
              <p className="mt-1 text-sm text-gray-500">{fieldSchema.description}</p>
            )}
          </div>
        );

      case 'imageUrl':
        return (
          <ImageURLField 
            key={fieldKey}
            {...commonProps}
            fieldLabel={fieldLabel}
            description={fieldSchema.description}
            value={value}
            onChange={handleValueChange}
          />
        );

      case 'url':
        return (
          <div key={fieldKey} {...commonProps}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {fieldLabel}
            </label>
            <input
              type="url"
              value={value || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="https://example.com"
            />
            {fieldSchema.description && (
              <p className="mt-1 text-sm text-gray-500">{fieldSchema.description}</p>
            )}
          </div>
        );

      case 'block':
        if (!fieldSchema.fields) return null;
        return (
          <div key={fieldKey} {...commonProps} className="mb-4 border p-4 rounded-md bg-gray-50">
            <h4 className="text-sm font-medium text-gray-700 mb-3">
              {fieldLabel}
            </h4>
            {Object.entries(fieldSchema.fields).map(([subFieldName, subFieldSchema]) => (
              renderField(
                subFieldName,
                subFieldSchema,
                value ? value[subFieldName] : undefined,
                fieldName
              )
            ))}
          </div>
        );

      case 'list':
        if (!fieldSchema.itemSchema) return null;

        const items = value || [];
        const getDefaultItem = () => {
          if (typeof fieldSchema.itemSchema === 'string') {
            return '';
          }
          if (typeof fieldSchema.itemSchema === 'object' && 'type' in fieldSchema.itemSchema) {
            const defaultItem: Record<string, any> = {};
            if (fieldSchema.itemSchema.fields) {
              Object.entries(fieldSchema.itemSchema.fields).forEach(([key, field]) => {
                if (field.type === 'block' && field.fields) {
                  defaultItem[key] = {};
                  Object.keys(field.fields).forEach(subKey => {
                    defaultItem[key][subKey] = '';
                  });
                } else {
                  defaultItem[key] = '';
                }
              });
            }
            return defaultItem;
          }
          return '';
        };

        return (
          <div key={fieldKey} {...commonProps}>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                {fieldLabel}
              </label>
              <button
                type="button"
                onClick={() => handleAddArrayItem(fieldName, getDefaultItem())}
                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-blue-600 hover:bg-blue-700"
              >
                Add Item
              </button>
            </div>

            {fieldSchema.description && (
              <p className="mt-1 text-sm text-gray-500 mb-2">{fieldSchema.description}</p>
            )}

            {items.length === 0 && (
              <div className="text-sm text-gray-500 mb-2">No items added yet</div>
            )}

            <div className="space-y-4">
              {items.map((item: any, index: number) => (
                <div key={index} className="border p-4 rounded-md bg-white">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-medium">{fieldLabel} {index + 1}</h4>
                    <button
                      type="button"
                      onClick={() => handleRemoveArrayItem(fieldName, index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>

                  {typeof fieldSchema.itemSchema === 'string' ? (
                    <input
                      type="text"
                      value={item || ''}
                      onChange={(e) => handleArrayItemChange(fieldName, index, fieldName, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  ) : (
                    <div className="space-y-3">
                      {fieldSchema.itemSchema?.fields && Object.entries(fieldSchema.itemSchema.fields).map(([subFieldName, subFieldSchema]) => (
                        <div key={subFieldName}>
                          {subFieldSchema.type === 'block' && subFieldSchema.fields ? (
                            <div className="border p-4 rounded-md bg-gray-50">
                              <h4 className="text-sm font-medium text-gray-700 mb-3">
                                {subFieldSchema.label || subFieldName}
                              </h4>
                              {Object.entries(subFieldSchema.fields).map(([nestedFieldName, nestedFieldSchema]) => (
                                renderField(
                                  `${subFieldName}.${nestedFieldName}`,
                                  nestedFieldSchema,
                                  item?.[subFieldName]?.[nestedFieldName],
                                  `${fieldName}.${index}`
                                )
                              ))}
                            </div>
                          ) : (
                            renderField(
                              subFieldName,
                              subFieldSchema,
                              item?.[subFieldName],
                              `${fieldName}.${index}`
                            )
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case 'productSelectorMultiple':
        return (
          <ProductChooser
            key={fieldKey}
            {...commonProps}
            fieldLabel={fieldLabel}
            fieldName={fieldName}
            value={value}
            onChange={handleValueChange}
            fieldSchema={fieldSchema}
            isMulti={true}
          />
        );

      case 'collectionSelectorMultiple':
        return (
          <CollectionChooser
            key={fieldKey}
            {...commonProps}
            fieldLabel={fieldLabel}
            fieldName={fieldName}
            value={value}
            onChange={handleValueChange}
            fieldSchema={fieldSchema}
            isMulti={true}
          />
        );

      case 'categorySelectorMultiple':
        return (
          <CategoryChooser
            key={fieldKey}
            {...commonProps}
            fieldLabel={fieldLabel}
            fieldName={fieldName}
            value={value}
            onChange={handleValueChange}
            fieldSchema={fieldSchema}
            isMulti={true}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {Object.entries(schema.fields).map(([fieldName, fieldSchema]) =>
        renderField(fieldName, fieldSchema, data[fieldName])
      )}
    </div>
  );
};

export default DynamicSectionEditor;